import React, { useState, useEffect } from "react";
import { EditButton, ShowButton, DeleteButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Input, Space, Tag, Card } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface Customer {
    id: number;
    tradeName?: string;
    vatNumber?: string;
    tpin?: string;
    name: string;
    description?: string;
    isActive: boolean;
    createdAt: string;
}

export const CustomerList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Customer[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "customers",
    });

    useEffect(() => {
        if (!data?.data) return;

        let customers = data.data as Customer[];
        
        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            customers = customers.filter((customer: Customer) =>
                customer.name.toLowerCase().includes(searchLower) ||
                customer.description?.toLowerCase().includes(searchLower) ||
                customer.tpin?.toLowerCase().includes(searchLower) ||
                customer.tradeName?.toLowerCase().includes(searchLower)
            );
        }

        // Sort by createdAt descending
        const sorted = [...customers].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns: ColumnsType<Customer> = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Trade Name",
            dataIndex: "tradeName",
            key: "tradeName",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['md'],
        },
        {
            title: "TPIN",
            dataIndex: "tpin",
            key: "tpin",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['lg'],
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            responsive: ['md'],
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Customers"
                icon={<TeamOutlined />}
                breadcrumbs={[{ title: "Customers" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/customers/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by name, trade name, or TPIN"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};
